import type { UserUiUpdateQuery } from "../Models/Ui/User";
import type { UserUpdateQuery } from "@/Modules/Models/Domain/User";
import type { UserApiUpdateQuery } from "@/Modules/Models/Api/User";

import { AppError } from "@/Modules/App/Models/Domain/AppError";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";
import { isAppError } from "@/Modules/App/Models/Typeguards/isAppError";

import { updateUser } from "@/Services/TherapyApi/Users";
import { isApiError } from "@/Services/Api/Typeguards/isApiError";

import UserApiToUserTransformer from "@/Modules/Transformers/User/UserApiToUserTransformer";

import useUserStore from "@/Modules/User/Store";
import useAppStore from "@/Modules/App/Store";

import { hasValue } from "@/Utils/Objects";

async function updateUserAH(userUiUpdateQuery: UserUiUpdateQuery) {
  const userStore = useUserStore();
  const appStore = useAppStore();
  const userApiToUserTransformer = new UserApiToUserTransformer();
  let userUpdateQuery: UserUpdateQuery = {};

  userStore.isLoading = true;
  userStore.notification = null;

  try {
    userUpdateQuery = transformUserUiUpdateQueryToUserUpdateQuery(userUiUpdateQuery);
  } catch (error: unknown) {
    if (isAppError(error)) {
      userStore.notification = {
        type: NotificationType.ERROR,
        message: error.i18nKey || error.message,
        code: null,
      };
    }

    userStore.isLoading = false;

    return;
  }

  try {
    const userApiUpdateQuery = transformUserUpdateQueryToUserUpdateApiQuery(userUpdateQuery);
    const userApiResponse = await updateUser(userApiUpdateQuery);

    userStore.user = userApiToUserTransformer.transform(userApiResponse);
    userStore.notification = {
      type: NotificationType.SUCCESS,
      message: "",
      code: null,
    };
  } catch (error: unknown) {
    const code = isApiError(error) ? error["error_code"] : null;

    if (code === ApiErrorCodes.EMAIL_ALREADY_EXIST) {
      userStore.notification = {
        type: NotificationType.ERROR,
        code,
        message: "account.email.error.this_email_already_exists.",
      };
    } else {
      appStore.showSnackbar({
        message: "there_was_an_error_connecting_to_the_server_please_try_again_later",
      });
    }
  } finally {
    userStore.isLoading = false;
  }
}

function transformUserUiUpdateQueryToUserUpdateQuery(userUiUpdateQuery: UserUiUpdateQuery): UserUpdateQuery {
  const userUpdateQuery: UserUpdateQuery = {};

  if (hasValue(userUiUpdateQuery.newEmail)) {
    if (
      hasValue(userUiUpdateQuery.confirmNewEmail) &&
      userUiUpdateQuery.newEmail !== userUiUpdateQuery.confirmNewEmail
    ) {
      throw new AppError("emails are different", "account.email.error.introduced_emails_are_not_the_same");
    }

    if (userUiUpdateQuery.currentEmail === userUiUpdateQuery.newEmail) {
      throw new AppError(
        "new email is the same as current email",
        "account.email.error.your_email_is_the_same_as_the_previous_one"
      );
    }

    userUpdateQuery.email = userUiUpdateQuery.newEmail;
  }

  if (hasValue(userUiUpdateQuery.username)) {
    if (userUiUpdateQuery.username === userUiUpdateQuery.currentUsername) {
      throw new AppError(
        "usernames are the same",
        "account.username.error.your_name_and_surname_is_the_same_as_the_current_one"
      );
    }

    userUpdateQuery.name = userUiUpdateQuery.username;
  }

  if (hasValue(userUiUpdateQuery.isNotificationActive)) {
    userUpdateQuery.isNotificationActive = userUiUpdateQuery.isNotificationActive;
  }

  if (hasValue(userUiUpdateQuery.description)) {
    userUpdateQuery.description = userUiUpdateQuery.description;
  }

  if (hasValue(userUiUpdateQuery.isWantTrial)) {
    userUpdateQuery.isWantTrial = userUiUpdateQuery.isWantTrial;
  }

  if (hasValue(userUiUpdateQuery.timeZone)) {
    userUpdateQuery.timeZone = userUiUpdateQuery.timeZone;
  }

  return userUpdateQuery;
}

function transformUserUpdateQueryToUserUpdateApiQuery(userUpdateQuery: UserUpdateQuery): UserApiUpdateQuery {
  const userApiUpdateQuery: UserApiUpdateQuery = {
    description: userUpdateQuery.description,
    email: userUpdateQuery.email,
    push_alert: userUpdateQuery.isNotificationActive,
    username: userUpdateQuery.name,
    is_want_trial: userUpdateQuery.isWantTrial,
    time_zone: userUpdateQuery.timeZone,
  };

  return userApiUpdateQuery;
}

export default updateUserAH;
